import React, { useRef, useEffect } from "react";

import { X } from "lucide-react";

export function Modal({
  title,
  close,
  children,
  wide = false,
  initialFocus,
}: {
  title: string;
  wide?: boolean;
  initialFocus?: string;
  close: () => void;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const target =
      (initialFocus
        ? ref.current?.querySelector<HTMLElement>(initialFocus)
        : null) ??
      ref.current?.querySelector<HTMLElement>(
        "input:not([disabled]),select:not([disabled]),textarea:not([disabled]),button:not([disabled])",
      );
    target?.focus();
    const keepFocus = (event: FocusEvent) => {
      if (event.target instanceof Node && !ref.current?.contains(event.target))
        target?.focus();
    };
    document.addEventListener("focusin", keepFocus);
    return () => {
      document.removeEventListener("focusin", keepFocus);
      requestAnimationFrame(() => {
        if (previous?.isConnected && previous !== document.body)
          previous.focus();
        else
          document
            .querySelector<HTMLElement>('[aria-label="Drawing tools"] button')
            ?.focus();
      });
    };
  }, []);
  useEffect(() => {
    if (!ref.current?.contains(document.activeElement))
      ref.current
        ?.querySelector<HTMLElement>("button:not([disabled])")
        ?.focus();
  });
  return (
    <div
      className="modal-backdrop"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <section
        ref={ref}
        className={`modal${wide ? " modal-wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            close();
          }
          if (e.key === "Tab") {
            const controls = Array.from(
              ref.current?.querySelectorAll<HTMLElement>(
                'button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),summary,a[href],[tabindex="0"]',
              ) || [],
            ).filter((el) => el.getClientRects().length > 0);
            const first = controls[0],
              last = controls[controls.length - 1];
            if (e.shiftKey && document.activeElement === first) {
              e.preventDefault();
              last?.focus();
            }
            if (!e.shiftKey && document.activeElement === last) {
              e.preventDefault();
              first?.focus();
            }
          }
          e.stopPropagation();
        }}
      >
        <button
          className="modal-close"
          aria-label="Close dialog"
          onClick={close}
        >
          <X size={20} />
        </button>
        <h2>{title}</h2>
        {children}
      </section>
    </div>
  );
}
